import { useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CloseIcon from "@mui/icons-material/Close";
import classes from "../../styles/DashboardView.module.css";
import { SourceBreakdownCompare, SourceCourseCompare, truncateTitle } from "../../utilities/dashboardAggregations";
import { numberWithCommas } from "../Oversigt";
import { IndexBadge } from "./IndexBadge";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f", "#0288d1", "#388e3c", "#f57c00"];
const ANDRE_COLOR = "#9aa0a6";
const TOP_N = 8;
const ANDRE_LABEL = "Andet";

type Metric = "count" | "revenue";

interface SourcePieChartProps {
    data: SourceBreakdownCompare[];
    currentYearLabel: string;
    prevYearLabel: string;
}

interface PieSlice extends SourceBreakdownCompare {
    value: number;
    prevValue: number;
    _bundled?: SourceBreakdownCompare[];
}

function valueFor(entry: SourceBreakdownCompare, metric: Metric): number {
    return metric === "count" ? entry.currentCount : entry.currentRevenue;
}

function prevValueFor(entry: SourceBreakdownCompare, metric: Metric): number {
    return metric === "count" ? entry.previousCount : entry.previousRevenue;
}

function mergeCoursesAcross(sources: SourceBreakdownCompare[]): SourceCourseCompare[] {
    const byTitle = new Map<string, SourceCourseCompare>();
    for (const s of sources) {
        for (const c of s.courses) {
            const existing = byTitle.get(c.title);
            if (existing) {
                existing.currentCount += c.currentCount;
                existing.previousCount += c.previousCount;
                existing.currentRevenue += c.currentRevenue;
                existing.previousRevenue += c.previousRevenue;
            } else {
                byTitle.set(c.title, { ...c });
            }
        }
    }
    return Array.from(byTitle.values()).sort(
        (a, b) => b.currentRevenue - a.currentRevenue || b.previousRevenue - a.previousRevenue,
    );
}

function buildSlices(data: SourceBreakdownCompare[], metric: Metric): PieSlice[] {
    const candidates = data.filter(d =>
        metric === "count" ? d.currentCount > 0 : d.currentRevenue > 0,
    );

    const sorted = [...candidates].sort((a, b) => valueFor(b, metric) - valueFor(a, metric));

    const visible = sorted.slice(0, TOP_N);
    const rest = sorted.slice(TOP_N);

    const slices: PieSlice[] = visible.map(d => ({
        ...d,
        value: valueFor(d, metric),
        prevValue: prevValueFor(d, metric),
    }));

    if (rest.length > 0) {
        const existingIdx = slices.findIndex(s => s.source === ANDRE_LABEL);
        if (existingIdx !== -1) {
            const existing = slices[existingIdx];
            const existingAsBreakdown: SourceBreakdownCompare = {
                source: existing.source,
                currentCount: existing.currentCount,
                previousCount: existing.previousCount,
                currentRevenue: existing.currentRevenue,
                previousRevenue: existing.previousRevenue,
                courses: existing.courses,
            };
            existing.currentCount += rest.reduce((s, r) => s + r.currentCount, 0);
            existing.previousCount += rest.reduce((s, r) => s + r.previousCount, 0);
            existing.currentRevenue += rest.reduce((s, r) => s + r.currentRevenue, 0);
            existing.previousRevenue += rest.reduce((s, r) => s + r.previousRevenue, 0);
            existing.courses = mergeCoursesAcross([existingAsBreakdown, ...rest]);
            existing.value = valueFor(existing, metric);
            existing.prevValue = prevValueFor(existing, metric);
            existing._bundled = rest;
        } else {
            const andre: PieSlice = {
                source: ANDRE_LABEL,
                currentCount: rest.reduce((s, r) => s + r.currentCount, 0),
                previousCount: rest.reduce((s, r) => s + r.previousCount, 0),
                currentRevenue: rest.reduce((s, r) => s + r.currentRevenue, 0),
                previousRevenue: rest.reduce((s, r) => s + r.previousRevenue, 0),
                courses: mergeCoursesAcross(rest),
                value: 0,
                prevValue: 0,
                _bundled: rest,
            };
            andre.value = valueFor(andre, metric);
            andre.prevValue = prevValueFor(andre, metric);
            slices.push(andre);
        }
    }

    return slices;
}

function formatKr(n: number): string {
    return `${numberWithCommas(n)} kr.`;
}

function pct(value: number, total: number): number {
    if (total <= 0) return 0;
    return (value / total) * 100;
}

function formatShareDelta(curPct: number, prevPct: number, hasPrev: boolean): string | null {
    if (!hasPrev) return null;
    const diff = Math.round((curPct - prevPct) * 10) / 10;
    if (Math.abs(diff) < 0.1) return "uændret";
    const sign = diff > 0 ? "↑ +" : "↓ ";
    return `${sign}${Math.abs(diff).toString().replace(".", ",")}%`;
}

export function SourcePieChart({ data, currentYearLabel, prevYearLabel }: SourcePieChartProps) {
    const [metric, setMetric] = useState<Metric>("count");
    const [dialogData, setDialogData] = useState<PieSlice | null>(null);

    const slices = useMemo(() => buildSlices(data, metric), [data, metric]);

    if (!data || data.length === 0 || slices.length === 0) {
        return (
            <Paper className={classes.chartCard} elevation={2}>
                <div className={classes.chartTitle}>Hvor har de hørt om os?</div>
                <p>Ingen data</p>
            </Paper>
        );
    }

    const totalCurrent = slices.reduce((sum, s) => sum + s.value, 0);
    const totalPrev = slices.reduce((sum, s) => sum + s.prevValue, 0);
    const hasPrev = totalPrev > 0;

    const renderLabel = (entry: any) => {
        const curPct = pct(entry.value, totalCurrent);
        const prevPct = pct(entry.prevValue, totalPrev);
        const delta = formatShareDelta(curPct, prevPct, hasPrev);
        const name = entry.source.length > 18 ? entry.source.substring(0, 18) + "…" : entry.source;
        if (metric === "count") {
            return `${name} ${curPct.toFixed(0)}%${delta ? ` ${delta}` : ""}`;
        }
        return `${name} ${numberWithCommas(entry.value)} kr. (${curPct.toFixed(0)}%)${delta ? ` ${delta}` : ""}`;
    };

    return (
        <Paper className={classes.chartCard} elevation={2}>
            <div className={classes.chartHeader}>
                <h3 className={classes.chartTitle}>
                    Hvor har de hørt om os?{" "}
                    <span style={{ fontSize: 12, color: "#8a8f96", fontWeight: 400 }}>
                        · {currentYearLabel} vs {prevYearLabel} · klik for detaljer
                    </span>
                </h3>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={metric}
                    onChange={(_, v) => v && setMetric(v)}
                    sx={{ marginLeft: "auto" }}
                >
                    <ToggleButton value="count" sx={{ textTransform: "none", fontSize: 12, py: 0.25, px: 1.5 }}>Tilmeldinger</ToggleButton>
                    <ToggleButton value="revenue" sx={{ textTransform: "none", fontSize: 12, py: 0.25, px: 1.5 }}>Omsætning</ToggleButton>
                </ToggleButtonGroup>
            </div>
            <ResponsiveContainer width="100%" height={560}>
                <PieChart>
                    <Pie
                        data={slices}
                        dataKey="value"
                        nameKey="source"
                        cx="50%"
                        cy="48%"
                        innerRadius={115}
                        outerRadius={210}
                        paddingAngle={1.5}
                        label={renderLabel}
                        labelLine={{ stroke: "#c8ccd3" }}
                        fontSize={12}
                        onClick={(p: any) => { if (p && p.payload) setDialogData(p.payload); }}
                        style={{ cursor: "pointer" }}
                    >
                        {slices.map((s, idx) => (
                            <Cell
                                key={`cell-${idx}`}
                                fill={s.source === ANDRE_LABEL ? ANDRE_COLOR : COLORS[idx % COLORS.length]}
                                stroke="#fff"
                                strokeWidth={2}
                            />
                        ))}
                    </Pie>
                    <Tooltip content={({ active, payload }: any) => {
                        if (!active || !payload || !payload[0]) return null;
                        const entry: PieSlice = payload[0].payload;
                        const curPct = pct(entry.value, totalCurrent);
                        const prevPct = pct(entry.prevValue, totalPrev);
                        const delta = formatShareDelta(curPct, prevPct, hasPrev);
                        return (
                            <div style={{ background: "white", border: "1px solid #ccc", padding: 10, borderRadius: 4, minWidth: 220 }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>{entry.source}</div>
                                <div style={{ fontSize: 12, color: "#5f6368", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>Tilmeldinger</div>
                                <div style={{ fontSize: 13, color: "#1976d2", fontWeight: 600 }}>
                                    {currentYearLabel}: {entry.currentCount} ({pct(entry.currentCount, data.reduce((s,d)=>s+d.currentCount,0)).toFixed(1).replace(".", ",")}%)
                                </div>
                                <div style={{ fontSize: 12, color: "#90caf9", fontWeight: 600 }}>{prevYearLabel}: {entry.previousCount}</div>
                                <IndexBadge current={entry.currentCount} previous={entry.previousCount} label="Indeks tilm." />
                                <div style={{ fontSize: 12, color: "#5f6368", marginTop: 8, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>Omsætning</div>
                                <div style={{ fontSize: 13, color: "#2e7d32", fontWeight: 600 }}>
                                    {currentYearLabel}: {formatKr(entry.currentRevenue)}
                                </div>
                                <div style={{ fontSize: 12, color: "#a5d6a7", fontWeight: 600 }}>{prevYearLabel}: {formatKr(entry.previousRevenue)}</div>
                                <IndexBadge current={entry.currentRevenue} previous={entry.previousRevenue} label="Indeks omsætn." />
                                {delta && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#5f6368", fontWeight: 500 }}>
                                        {delta} af kagen ({metric === "count" ? "tilm." : "omsætn."})
                                    </div>
                                )}
                                {entry.source === ANDRE_LABEL && entry._bundled && (
                                    <div style={{ marginTop: 6, fontSize: 11, color: "#5f6368", borderTop: "1px solid #eee", paddingTop: 6 }}>
                                        Bundter {entry._bundled.length} mindre kilder · klik for detaljer
                                    </div>
                                )}
                            </div>
                        );
                    }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ResponsiveContainer>

            <Dialog open={dialogData !== null} onClose={() => setDialogData(null)} maxWidth="md" fullWidth>
                {dialogData && (
                    <>
                        <DialogTitle sx={{ pb: 0.5 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 16, fontWeight: 600 }}>{dialogData.source}</span>
                                <IconButton onClick={() => setDialogData(null)} size="small"><CloseIcon fontSize="small" /></IconButton>
                            </div>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                                <div style={{ padding: 12, background: "#f5f8fc", border: "1px solid #dde6f2", borderRadius: 4 }}>
                                    <div style={{ fontSize: 11, color: "#5f6368", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Tilmeldinger</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: "#1976d2" }}>{dialogData.currentCount}</div>
                                    <div style={{ fontSize: 12, color: "#90caf9", fontWeight: 600 }}>{prevYearLabel}: {dialogData.previousCount}</div>
                                    <IndexBadge current={dialogData.currentCount} previous={dialogData.previousCount} fontSize={13} />
                                </div>
                                <div style={{ padding: 12, background: "#f6fbf6", border: "1px solid #e0ede0", borderRadius: 4 }}>
                                    <div style={{ fontSize: 11, color: "#5f6368", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Omsætning</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: "#2e7d32" }}>{formatKr(dialogData.currentRevenue)}</div>
                                    <div style={{ fontSize: 12, color: "#a5d6a7", fontWeight: 600 }}>{prevYearLabel}: {formatKr(dialogData.previousRevenue)}</div>
                                    <IndexBadge current={dialogData.currentRevenue} previous={dialogData.previousRevenue} fontSize={13} />
                                </div>
                            </div>

                            {dialogData._bundled && dialogData._bundled.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 12, color: "#5f6368", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                                        Bundlede kilder ({dialogData._bundled.length})
                                    </div>
                                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr style={{ textAlign: "left", color: "#5f6368", borderBottom: "1px solid #eee" }}>
                                                <th style={{ padding: "6px 4px" }}>Kilde</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Tilm. {currentYearLabel}</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Tilm. {prevYearLabel}</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Indeks</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Omsætn. {currentYearLabel}</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Omsætn. {prevYearLabel}</th>
                                                <th style={{ padding: "6px 4px", textAlign: "right" }}>Indeks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dialogData._bundled.map((b, i) => (
                                                <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                    <td style={{ padding: "6px 4px" }}>{b.source}</td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{b.currentCount}</td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#8a8f96" }}>{b.previousCount}</td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right" }}><IndexBadge current={b.currentCount} previous={b.previousCount} label="" fontSize={11} inline /></td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatKr(b.currentRevenue)}</td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#8a8f96" }}>{formatKr(b.previousRevenue)}</td>
                                                    <td style={{ padding: "6px 4px", textAlign: "right" }}><IndexBadge current={b.currentRevenue} previous={b.previousRevenue} label="" fontSize={11} inline /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div style={{ fontSize: 12, color: "#5f6368", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                                Kurser ({dialogData.courses.length})
                            </div>
                            <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ textAlign: "left", color: "#5f6368", borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "white" }}>
                                            <th style={{ padding: "6px 4px" }}>Kursus</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Tilm. {currentYearLabel}</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Tilm. {prevYearLabel}</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Indeks</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Omsætn. {currentYearLabel}</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Omsætn. {prevYearLabel}</th>
                                            <th style={{ padding: "6px 4px", textAlign: "right" }}>Indeks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dialogData.courses.map((c, i) => (
                                            <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                <td style={{ padding: "6px 4px" }} title={c.title}>{truncateTitle(c.title, 45)}</td>
                                                <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.currentCount}</td>
                                                <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#8a8f96" }}>{c.previousCount}</td>
                                                <td style={{ padding: "6px 4px", textAlign: "right" }}><IndexBadge current={c.currentCount} previous={c.previousCount} label="" fontSize={11} inline /></td>
                                                <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatKr(c.currentRevenue)}</td>
                                                <td style={{ padding: "6px 4px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#8a8f96" }}>{formatKr(c.previousRevenue)}</td>
                                                <td style={{ padding: "6px 4px", textAlign: "right" }}><IndexBadge current={c.currentRevenue} previous={c.previousRevenue} label="" fontSize={11} inline /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Paper>
    );
}
